import * as React from 'react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';

/**
 * Field — wrapper for a form field (label + control + description + error).
 * Vertical stack with consistent gap.
 *
 * @param {object} props
 * @param {'vertical'|'horizontal'} [props.orientation='vertical']
 */
const Field = React.forwardRef(({ className, orientation = 'vertical', ...props }, ref) => (
  <div
    ref={ref}
    role="group"
    data-slot="field"
    data-orientation={orientation}
    className={cn('c-field', orientation === 'horizontal' ? 'flex flex-row items-center gap-3' : 'flex flex-col gap-2', className)}
    {...props}
  />
));
Field.displayName = 'Field';

/**
 * FieldLabel — label associated with a field control.
 * Wraps the existing Label primitive.
 */
const FieldLabel = React.forwardRef(({ className, ...props }, ref) => (
  <Label
    ref={ref}
    data-slot="field-label"
    className={cn('c-field-label text-primary', className)}
    {...props}
  />
));
FieldLabel.displayName = 'FieldLabel';

/**
 * FieldDescription — helper / hint text below a field control.
 */
const FieldDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="field-description"
    className={cn('c-field-description', 'mb-0 text-[13px] font-normal text-neutral', className)}
    {...props}
  />
));
FieldDescription.displayName = 'FieldDescription';

/**
 * FieldGroup — groups multiple Field components in a column with larger gap.
 */
const FieldGroup = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="field-group"
    className={cn('c-field-group', 'flex flex-col gap-4', className)}
    {...props}
  />
));
FieldGroup.displayName = 'FieldGroup';

/**
 * FieldSet — semantic <fieldset> wrapper for grouping related fields.
 */
const FieldSet = React.forwardRef(({ className, ...props }, ref) => (
  <fieldset
    ref={ref}
    data-slot="field-set"
    className={cn('c-fieldset', 'flex flex-col gap-3 border-0 p-0', className)}
    {...props}
  />
));
FieldSet.displayName = 'FieldSet';

/**
 * FieldTitle — heading for a fieldset / field group.
 * Shares `data-slot="field-label"` semantic with FieldLabel for grouping consistency.
 */
const FieldTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    data-slot="field-label"
    className={cn('c-field-title', 'text-base font-semibold text-primary-dark', className)}
    {...props}
  />
));
FieldTitle.displayName = 'FieldTitle';

/**
 * FieldContent — content area inside a Field (pairs with FieldLabel).
 */
const FieldContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="field-content"
    className={cn('c-field-content', 'flex flex-col gap-1', className)}
    {...props}
  />
));
FieldContent.displayName = 'FieldContent';

/**
 * FieldError — validation error message displayed below a field control.
 *
 * Two modes:
 * 1. Pass children as raw text/JSX → renders <p>...</p>
 * 2. Pass `errors` prop as array of `{ message }` objects → renders single <p> for 1 error
 *    or <ul><li>...</li></ul> for multiple errors
 *
 * Returns null when there are no children AND no valid errors array.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children]
 * @param {Array<{ message: string }>|null|undefined} [props.errors]
 */
const FieldError = React.forwardRef(({ className, children, errors, ...props }, ref) => {
  const hasErrors = Array.isArray(errors) && errors.length > 0;
  const hasChildren = children !== undefined && children !== null && children !== false;

  if (!hasErrors && !hasChildren) return null;

  if (hasErrors && errors.length === 1) {
    return (
      <p
        ref={ref}
        role="alert"
        data-slot="field-error"
        className={cn('c-field-error', 'mb-0 text-xs font-medium text-danger', className)}
        {...props}
      >
        {errors[0].message}
      </p>
    );
  }

  if (hasErrors && errors.length > 1) {
    return (
      <ul
        ref={ref}
        role="alert"
        data-slot="field-error"
        className={cn('c-field-error', 'mb-0 list-disc pl-4 text-xs font-medium text-danger', className)}
        {...props}
      >
        {errors.map((err, idx) => (
          <li key={idx}>{err.message}</li>
        ))}
      </ul>
    );
  }

  return (
    <p
      ref={ref}
      role="alert"
      data-slot="field-error"
      className={cn('c-field-error', 'mb-0 text-xs font-medium text-danger', className)}
      {...props}
    >
      {children}
    </p>
  );
});
FieldError.displayName = 'FieldError';

export { Field, FieldLabel, FieldDescription, FieldGroup, FieldSet, FieldTitle, FieldContent, FieldError };
